/**
 * Branding preview (#851) — shared helpers for the admin Branding card's
 * live preview frames and the `/admin/branding-preview/[theme]` page they
 * load.
 *
 * The page is the only one framed by the app itself, so the path lives here
 * for both the middleware (CSP `frame-ancestors 'self'`) and ThemeProvider
 * (forces the frame's theme regardless of the admin's own choice).
 */

export const BRANDING_PREVIEW_PATH = '/admin/branding-preview';

export type BrandingPreviewTheme = 'light' | 'dark';

const HEX_COLOUR_RE = /^#[0-9a-f]{6}$/i;

/** True for the preview page and anything beneath it, never for lookalike prefixes. */
export function isBrandingPreviewPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return pathname === BRANDING_PREVIEW_PATH || pathname.startsWith(`${BRANDING_PREVIEW_PATH}/`);
}

/** The theme a preview frame must show, taken from its path; `undefined` elsewhere. */
export function brandingPreviewThemeFromPath(
  pathname: string | null | undefined,
): BrandingPreviewTheme | undefined {
  if (!isBrandingPreviewPath(pathname)) return undefined;
  const segment = pathname!.slice(BRANDING_PREVIEW_PATH.length + 1).split('/')[0];
  return segment === 'light' || segment === 'dark' ? segment : undefined;
}

/** Accepts only a full `#rrggbb` colour; anything else becomes `null`. */
export function parsePreviewColour(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return HEX_COLOUR_RE.test(trimmed) ? trimmed.toLowerCase() : null;
}

/** URL for one preview frame. Invalid colours are left out so the page uses its defaults. */
export function buildBrandingPreviewSrc(
  theme: BrandingPreviewTheme,
  primary: string | null | undefined,
  secondary: string | null | undefined,
): string {
  const params = new URLSearchParams();
  const p = parsePreviewColour(primary);
  const s = parsePreviewColour(secondary);
  if (p) params.set('primary', p);
  if (s) params.set('secondary', s);
  const query = params.toString();
  return `${BRANDING_PREVIEW_PATH}/${theme}${query ? `?${query}` : ''}`;
}
